// World Map: every place is a region. Locked regions sit under fog until she has the stars.
// Phase 2 scaffold; Phase 3 makes it walkable.

import { h } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { drawSprite } from '@avatar/render';
import { getSave, totalStars, subscribe } from '../state';
import { GAMES, lockInfo } from '../registry';
import { topBar, toast } from '../ui';
import { go } from '../router';
import { activePet, drawPet } from '../pets';
import { drawArt, hasArt, onArtReady } from '@kit/assets';

interface Region {
  id: string; name: string; x: number; y: number; rx: number; ry: number;
  color: string; stars: number; to: string; blurb: string;
  /** games this region hosts; empty = a place, not a game family */
  filter?: (g: typeof GAMES[number]) => boolean;
}

export const REGIONS: Region[] = [
  { id: 'camp', name: 'Base Camp', x: 0.5, y: 0.84, rx: 0.17, ry: 0.08, color: '#9ad67e', stars: 0, to: 'hub', blurb: 'Home.' },
  { id: 'town', name: 'Town', x: 0.18, y: 0.78, rx: 0.15, ry: 0.07, color: '#f6d9b8', stars: 0, to: 'town', blurb: 'Neighbors with jobs.' },
  { id: 'academy', name: 'Academy', x: 0.5, y: 0.6, rx: 0.16, ry: 0.07, color: '#bcd3f5', stars: 0, to: 'academy/all', blurb: 'Every learning game.' },
  { id: 'wilds', name: 'The Wilds', x: 0.83, y: 0.74, rx: 0.14, ry: 0.08, color: '#f4c08a', stars: 0, to: 'wilds', blurb: 'Fun games.' },
  { id: 'woods', name: 'Word Woods', x: 0.2, y: 0.46, rx: 0.16, ry: 0.09, color: '#cdb6ee', stars: 12, to: 'academy/words', blurb: 'Where the word games grow.', filter: g => g.category === 'words' },
  { id: 'mines', name: 'Number Mines', x: 0.8, y: 0.48, rx: 0.15, ry: 0.09, color: '#b7cdf2', stars: 12, to: 'academy/math', blurb: 'Dig for math.', filter: g => g.category === 'math' },
  { id: 'volcano', name: 'Volcano Isle', x: 0.27, y: 0.2, rx: 0.16, ry: 0.1, color: '#f0b0a6', stars: 20, to: 'academy/science', blurb: 'Rocks, fossils and fire.', filter: g => g.category === 'science' && g.skills.some(s => s === 'geology' || s === 'paleontology' || s === 'earth_science') },
  { id: 'harbor', name: 'Star Harbor', x: 0.74, y: 0.17, rx: 0.17, ry: 0.1, color: '#9fc9e8', stars: 30, to: 'academy/science', blurb: 'Space, sea and sky.', filter: g => g.category === 'science' && g.skills.some(s => s === 'space' || s === 'physics' || s === 'chemistry' || s === 'biology') },
];

/** Where each region sits in the painted map (scene.map). Used when the art is loaded. */
const PAINTED: Record<string, { x: number; y: number; rx: number; ry: number }> = {
  camp: { x: 0.55, y: 0.75, rx: 0.17, ry: 0.07 },
  town: { x: 0.21, y: 0.61, rx: 0.17, ry: 0.07 },
  academy: { x: 0.55, y: 0.35, rx: 0.15, ry: 0.07 },
  wilds: { x: 0.82, y: 0.58, rx: 0.15, ry: 0.09 },
  woods: { x: 0.20, y: 0.37, rx: 0.17, ry: 0.08 },
  mines: { x: 0.81, y: 0.33, rx: 0.14, ry: 0.07 },
  volcano: { x: 0.19, y: 0.12, rx: 0.16, ry: 0.07 },
  harbor: { x: 0.76, y: 0.13, rx: 0.17, ry: 0.07 },
};
function regions(): Region[] {
  return hasArt('scene.map') ? REGIONS.map(r => ({ ...r, ...PAINTED[r.id] })) : REGIONS;
}

export function mapScreen(root: HTMLElement) {
  const bar = topBar('World Map', { back: 'hub' });
  const stage = h('div', { style: { padding: '0 12px' } });
  const hint = h('div.msg', null, 'Tap a region to go there. Foggy places open as you earn stars.');
  root.append(bar, stage, hint);

  // 2:3 to match the painted map; the procedural map is laid out in fractions so it fits too
  const S = makeSurface(stage, { aspect: 1.5, draw: false, onResize: draw });

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      const W = S.w, H = S.h;
      let best: Region | null = null, bd = 1e9;
      for (const r of regions()) {
        const d = Math.hypot((p.x - r.x * W) / (r.rx * W), (p.y - r.y * H) / (r.ry * H));
        if (d < bd) { bd = d; best = r; }
      }
      if (!best || bd > 1.35) return;
      const stars = totalStars();
      if (stars < best.stars) { toast(`${best.name} opens at ${best.stars} stars. You have ${stars}.`); return; }
      if (best.filter && !GAMES.some(g => best!.filter!(g) && lockInfo(g).unlocked)) { toast(`${best.name} is quiet for now. New games arrive soon.`); return; }
      // themed regions have their own page with a guide and a daily quest
      go(best.filter ? `region/${best.id}` : best.to);
    },
  });

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    const s = getSave();
    const stars = totalStars(s);
    const painted = drawArt(c, 'scene.map', 0, 0, W, H);
    const REGIONS = regions();
    const camp = REGIONS[0];
    if (!painted) {
      // sea and land
      c.fillStyle = '#9fd3e8'; c.fillRect(0, 0, W, H);
      c.fillStyle = '#cfe6b3';
      c.beginPath(); c.moveTo(0, H * 0.34); c.bezierCurveTo(W * 0.2, H * 0.28, W * 0.5, H * 0.4, W, H * 0.33); c.lineTo(W, H); c.lineTo(0, H); c.closePath(); c.fill();
      // islands in the north
      for (const r of REGIONS.filter(r => r.y < 0.3)) { c.fillStyle = '#cfe6b3'; c.beginPath(); c.ellipse(r.x * W, r.y * H, r.rx * W * 1.25, r.ry * H * 1.35, 0, 0, 7); c.fill(); }
      // roads from camp
      c.strokeStyle = '#e4cfa0'; c.lineWidth = Math.max(6, W * 0.02); c.lineCap = 'round';
      for (const r of REGIONS.slice(1)) {
        if (r.y < 0.3) continue;
        c.beginPath(); c.moveTo(camp.x * W, camp.y * H); c.quadraticCurveTo((camp.x + r.x) / 2 * W, Math.max(camp.y, r.y) * H, r.x * W, r.y * H); c.stroke();
      }
    }
    // regions
    for (const r of REGIONS) {
      const locked = stars < r.stars;
      const cx = r.x * W, cy = r.y * H, rx = r.rx * W, ry = r.ry * H;
      if (!painted) { c.fillStyle = locked ? '#b9b9bf' : r.color; c.beginPath(); c.ellipse(cx, cy, rx, ry, 0, 0, 7); c.fill(); }
      if (locked) {
        c.fillStyle = painted ? 'rgba(200,200,205,.8)' : 'rgba(255,255,255,.55)'; c.beginPath(); c.ellipse(cx, cy, rx, ry, 0, 0, 7); c.fill();
        c.setLineDash([6, 6]); c.strokeStyle = '#8a8a93'; c.lineWidth = 2; c.stroke(); c.setLineDash([]);
      } else if (!painted) {
        // a little landmark per region
        landmark(c, r.id, cx, cy - ry * 0.35, Math.min(rx, ry * 1.6) * 0.5);
      }
      c.fillStyle = '#2a2a2e';
      c.font = `800 ${Math.round(Math.max(12, W * 0.036))}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
      roundRect(c, cx - rx * 0.9, cy + ry * 0.45, rx * 1.8, Math.max(20, W * 0.055), 6); c.fillStyle = 'rgba(255,255,255,.85)'; c.fill();
      c.fillStyle = '#2a2a2e';
      c.fillText(locked ? `${r.name}  ${r.stars}★` : r.name, cx, cy + ry * 0.45 + Math.max(10, W * 0.028));
    }
    // her at base camp
    const sc = Math.max(1.6, W / 260);
    const hx = (camp.x + camp.rx * 0.55) * W, hy = camp.y * H + 4;
    drawSprite(c, s.avatar, hx, hy, sc, 0, 'down');
    const pet = activePet(s);
    if (pet) drawPet(c, pet, hx + sc * 10, hy + 2, sc);
  }

  function landmark(c: CanvasRenderingContext2D, id: string, x: number, y: number, r: number) {
    switch (id) {
      case 'camp': c.fillStyle = '#f6efdc'; c.fillRect(x - r * 0.6, y - r * 0.2, r * 1.2, r * 0.9); c.fillStyle = '#e2574c'; c.beginPath(); c.moveTo(x - r * 0.8, y - r * 0.2); c.lineTo(x, y - r); c.lineTo(x + r * 0.8, y - r * 0.2); c.fill(); break;
      case 'town': for (let i = -1; i <= 1; i++) { c.fillStyle = ['#e2574c', '#4f8fe6', '#8a5a3b'][i + 1]; c.fillRect(x + i * r * 0.7 - r * 0.25, y - r * 0.1, r * 0.5, r * 0.7); } break;
      case 'academy': c.fillStyle = '#f5c542'; c.beginPath(); c.arc(x, y, r * 0.45, Math.PI, 0); c.lineTo(x + r * 0.55, y + r * 0.35); c.lineTo(x - r * 0.55, y + r * 0.35); c.fill(); break;
      case 'wilds': c.fillStyle = '#4f9a4a'; for (let i = -1; i <= 1; i++) { c.beginPath(); c.moveTo(x + i * r * 0.7 - r * 0.4, y + r * 0.6); c.lineTo(x + i * r * 0.7, y - r * 0.6); c.lineTo(x + i * r * 0.7 + r * 0.4, y + r * 0.6); c.fill(); } break;
      case 'woods': c.fillStyle = '#7d5ba6'; for (let i = -1; i <= 1; i++) { c.beginPath(); c.arc(x + i * r * 0.7, y, r * 0.45, 0, 7); c.fill(); } c.fillStyle = '#f6f3ec'; c.font = `900 ${Math.round(r * 0.6)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('A', x - r * 0.7, y); c.fillText('B', x, y); c.fillText('C', x + r * 0.7, y); break;
      case 'mines': c.fillStyle = '#6b6b75'; c.beginPath(); c.moveTo(x - r, y + r * 0.6); c.lineTo(x, y - r * 0.8); c.lineTo(x + r, y + r * 0.6); c.fill(); c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(x, y + r * 0.3, r * 0.3, Math.PI, 0); c.fill(); break;
      case 'volcano': c.fillStyle = '#8a5a3b'; c.beginPath(); c.moveTo(x - r, y + r * 0.6); c.lineTo(x - r * 0.3, y - r * 0.7); c.lineTo(x + r * 0.3, y - r * 0.7); c.lineTo(x + r, y + r * 0.6); c.fill(); c.fillStyle = '#e2574c'; c.beginPath(); c.arc(x, y - r * 0.8, r * 0.3, 0, 7); c.fill(); break;
      case 'harbor': c.fillStyle = '#f5c542'; c.beginPath(); for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5; const rr = i % 2 ? r * 0.3 : r * 0.7; c.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); } c.closePath(); c.fill(); break;
    }
  }

  draw();
  const un = subscribe(draw);
  const unArt = onArtReady(draw);
  return () => { un(); unArt(); unPointer(); S.destroy(); };
}
